// @givmo/org-console — the console's ASSISTANT SLOT in the top-right cluster (Org Machinery OM-31).
//
// ══ ONE ENTRY, REGISTERED BY A MODULE ══════════════════════════════════════════════════════════
//
// The cluster is the suite's furniture — `[gear][waffle][avatar]` on every page — and its law is that
// an icon renders only where its destination is REAL. An assistant is a real destination only where a
// module has built one and switched it on, so the shell does not own an assistant: it owns a SLOT,
// and a module registers the ONE entry it holds (the first is a Donations module's assistant,
// EP-AI1, dark until its knob flips). The entry renders the whole control — its labelled button AND
// the panel it opens — so the panel can overlay any page while its open state belongs to the
// control. With nothing registered
// the slot renders NOTHING: no hidden icon, no disabled button, no wrapper element, so a console whose
// modules register nothing draws exactly the cluster it drew before this slot existed.
//
// ONE entry, deliberately: a second registration REPLACES the first rather than stacking a second
// assistant into the cluster. D-EP-44 ruled one centralized assistant capability with the panel a
// class-blind package (`@givmo/assistant-panel`), so a second control beside the first would be two
// assistants in one bar. And class-blind: nothing here names a class or a module — the registering
// host code decides what registers and when, and passes `null` to withdraw it (its knob off).
//
// The seam is `apps/admin`'s reserved slot (`components/assistant-slot.tsx`, EP-DES2 Wave 0) built
// ONCE here for the console shell: a module-level registration read through `useSyncExternalStore`,
// so an entry registered after the shell mounted still appears without a reload.

import { useSyncExternalStore, type ComponentType } from 'react';

/** A registered entry renders the whole control — its glyph, its label and its own panel. */
export type ConsoleAssistantEntry = ComponentType;

let entry: ConsoleAssistantEntry | null = null;
const subscribers = new Set<() => void>();

/**
 * Register the console's ONE assistant entry — or, with `null`, withdraw it (a knob that is off, or a
 * module that is not here). A later registration replaces an earlier one.
 */
export function registerConsoleAssistant(next: ConsoleAssistantEntry | null): void {
  entry = next;
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

function getSnapshot(): ConsoleAssistantEntry | null {
  return entry;
}

/**
 * The slot itself, mounted by a host FIRST in its top-right cluster. Renders the registered entry, or
 * nothing at all.
 */
export function ConsoleAssistantSlot() {
  const Entry = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return Entry === null ? null : <Entry />;
}

// ══ THE DOCK: THE WIDTH THE PANEL TAKES BESIDE THE PAGE ════════════════════════════════════════
//
// On a wide screen the registered control docks its panel at the right edge, full height
// (`@givmo/assistant-panel`'s `AssistantPanelFrame`), and publishes the width it takes here; the shell
// reserves exactly that on its main column (top bar included), so the page reflows into what is left
// instead of sitting under the panel. `null` — the default, a closed panel, a narrow screen, or no
// assistant on this page — reserves nothing.

let dockWidth: number | null = null;
const dockSubscribers = new Set<() => void>();

/** Publish the width the docked panel takes beside the page, or `null` for none. */
export function setConsoleAssistantDock(width: number | null): void {
  if (dockWidth === width) return;
  dockWidth = width;
  for (const notify of dockSubscribers) notify();
}

function subscribeDock(notify: () => void): () => void {
  dockSubscribers.add(notify);
  return () => {
    dockSubscribers.delete(notify);
  };
}

function getDockSnapshot(): number | null {
  return dockWidth;
}

/** The width the shell reserves beside its main column for the docked panel, or `null`. */
export function useConsoleAssistantDock(): number | null {
  return useSyncExternalStore(subscribeDock, getDockSnapshot, () => null);
}
